import { useState } from 'react';
import { useUIStore } from '@/stores';
import { m, AnimatePresence } from 'framer-motion';
import { Menu, X, Facebook, Instagram, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from "react-i18next";

interface MenuItemType {
    label: string;
    link?: string;
    ariaLabel?: string;
    children?: MenuItemType[];
    external?: boolean;
}

interface MenuItemProps {
    item: MenuItemType;
    color: string;
    onClick: () => void;
}

const MenuItem = ({ item, color, onClick }: MenuItemProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasChildren = item.children && item.children.length > 0;

    return (
        <m.li
            variants={{
                open: { y: 0, opacity: 1, transition: { y: { stiffness: 1000, velocity: -100 } } },
                closed: { y: 50, opacity: 0, transition: { y: { stiffness: 1000 } } }
            }}
            className="w-full text-center"
        >
            {hasChildren ? (
                <div className="flex flex-col items-center">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="flex items-center justify-center gap-2 text-3xl font-bold py-2 hover:text-opacity-80 transition-colors font-display tracking-tight w-full"
                        style={{ color }}
                    >
                        {item.label}
                        {isOpen ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
                    </button>
                    <AnimatePresence>
                        {isOpen && (
                            <m.ul
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden w-full flex flex-col items-center space-y-2 bg-gray-50/50 rounded-2xl"
                            >
                                {item.children!.map((child, idx) => (
                                    <li key={idx} className="w-full">
                                        {child.external ? (
                                            <a
                                                href={child.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block text-xl font-medium py-2 text-gray-600 hover:text-primary transition-colors"
                                                onClick={onClick}
                                            >
                                                {child.label}
                                            </a>
                                        ) : (
                                            <Link
                                                to={child.link!}
                                                className="block text-xl font-medium py-2 text-gray-600 hover:text-primary transition-colors"
                                                onClick={onClick}
                                            >
                                                {child.label}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </m.ul>
                        )}
                    </AnimatePresence>
                </div>
            ) : item.link?.startsWith('/') ? (
                <Link
                    to={item.link}
                    className="block text-3xl font-bold py-2 hover:text-opacity-80 transition-colors font-display tracking-tight"
                    style={{ color }}
                    onClick={onClick}
                    aria-label={item.ariaLabel}
                >
                    {item.label}
                </Link>
            ) : (
                <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-3xl font-bold py-2 hover:text-opacity-80 transition-colors font-display tracking-tight"
                    style={{ color }}
                    onClick={onClick}
                    aria-label={item.ariaLabel}
                >
                    {item.label}
                </a>
            )}
        </m.li>
    );
};

interface StaggeredMenuProps {
    items: MenuItemType[];
    socialItems?: { label: string; link: string }[];
    colors?: string[];
    menuButtonColor?: string;
    openMenuButtonColor?: string;
    logoUrl?: string;
}

const StaggeredMenu = ({
    items,
    socialItems = [],
    colors = ['#D32F2F', '#111'],
    menuButtonColor = '#000',
    openMenuButtonColor = '#000',
    logoUrl
}: StaggeredMenuProps) => {
    const { t } = useTranslation();
    const { isMobileMenuOpen, setMobileMenuOpen } = useUIStore();

    const toggleMenu = () => setMobileMenuOpen(!isMobileMenuOpen);

    return (
        <div className="relative">
            {/* Toggle Button */}
            <button
                onClick={toggleMenu}
                className="relative z-50 p-2 rounded-full transition-colors duration-300 hover:bg-black/5"
                style={{ color: isMobileMenuOpen ? openMenuButtonColor : menuButtonColor }}
                aria-label={isMobileMenuOpen ? "Zamknij menu" : "Otwórz menu"}
            >
                {isMobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
            </button>

            {/* Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <m.div
                        initial="closed"
                        animate="open"
                        exit="closed"
                        variants={{
                            open: {
                                opacity: 1,
                                height: "100vh",
                                transition: { staggerChildren: 0.07, delayChildren: 0.1 }
                            },
                            closed: {
                                opacity: 0,
                                height: 0,
                                transition: { staggerChildren: 0.05, staggerDirection: -1, delay: 0.3 }
                            }
                        }}
                        className="fixed inset-0 top-0 left-0 bg-white z-40 flex flex-col justify-center items-center overflow-hidden"
                        style={{
                            height: '100dvh',
                            width: '100vw'
                        }}
                    >
                        {/* Logo */}
                        {logoUrl && (
                            <m.img
                                variants={{
                                    open: { opacity: 1, y: 0 },
                                    closed: { opacity: 0, y: -20 }
                                }}
                                src={logoUrl}
                                alt={t('logo')}
                                className="h-16 w-auto mb-12 absolute top-8 z-10"
                            />
                        )}

                        <div className="w-full h-full overflow-y-auto custom-scrollbar flex flex-col justify-start items-center pt-32 pb-32">
                            <ul className="flex flex-col items-center space-y-6 w-full max-w-sm px-4 list-none p-0 m-0 mt-auto mb-auto">
                                {items.map((item, index) => (
                                    <MenuItem
                                        key={index}
                                        item={item}
                                        color={colors[index % colors.length]}
                                        onClick={toggleMenu}
                                    />
                                ))}
                            </ul>
                        </div>

                        {/* Social Icons */}
                        <m.div
                            variants={{
                                open: { opacity: 1, y: 0 },
                                closed: { opacity: 0, y: 20 }
                            }}
                            className="absolute bottom-12 flex space-x-6"
                        >
                            {socialItems.map((social, index) => (
                                <a
                                    key={index}
                                    href={social.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-400 hover:text-gray-900 transition-colors p-2"
                                >
                                    <span className="sr-only">{social.label}</span>
                                    {social.label.toLowerCase().includes('facebook') ? (
                                        <Facebook className="w-6 h-6" />
                                    ) : social.label.toLowerCase().includes('instagram') ? (
                                        <Instagram className="w-6 h-6" />
                                    ) : (
                                        social.label
                                    )}
                                </a>
                            ))}
                        </m.div>
                    </m.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default StaggeredMenu;
